import { useEffect, useMemo, useRef, useState } from "react";
import { X, Upload, RotateCcw, Check, Search, ImageIcon, Download } from "lucide-react";
import { CATALOG } from "./catalog.js";
import { IMG_W, IMG_H, ISO_W, ISO_H, CUBE_H, BASE, TILE_FILES, GROUND_TYPES } from "./tileset.js";
import { overrideFor, refreshOverrides, measureBBox } from "./overrides.js";
import Sprite from "./Sprite.jsx";
import api from "@/lib/api";
import { t } from "@/i18n";

// ─────────────────────────────────────────────────────────────
// IMAGES DES BÂTIMENTS — pensée pour ne jamais toucher au code.
//   1. choisir un bâtiment
//   2. déposer une image
//   3. régler la hauteur (losange) et, si besoin, la taille
//
// CE QUI CHANGE : les structures de PLUSIEURS cases (grande médersa
// 2×3, halle 2×2, rempart 3×1…) étaient exclues de cette liste. Elles
// y sont maintenant, avec exactement la même interface : l'image
// remplace la structure entière — un seul dessin au lieu de plusieurs
// blocs — et un curseur « taille » règle son agrandissement, préréglé
// sur la largeur de l'emprise. Le moteur de rendu suit la même règle
// (voir IsoIsland, overrideVisual).
// ─────────────────────────────────────────────────────────────

const ANCHOR_X = IMG_W / 2 - 0.5;
const DEFAULT_ANCHOR = 182.5 + CUBE_H; // un cran au-dessus du sol par défaut

export default function AdminImages({ onClose, onChanged }) {
  // Tous les objets à image, quelle que soit leur emprise — PLUS les
  // textures peintes de l'éditeur de terrain (terre, herbe, dalles…).
  // Elles n'étaient pas modifiables parce qu'elles ne sont pas des
  // objets du catalogue : ce sont des sols peints, stockés comme un
  // simple identifiant sur la case. On les expose ici sous un
  // identifiant réservé `ground_<id>`, et le moteur de rendu regarde
  // cet identifiant avant de dessiner une case de terrain.
  const editable = useMemo(() => [
    ...CATALOG.filter((i) => i.kind === "tile"),
    ...GROUND_TYPES.map((g) => ({
      id: `ground_${g.id}`,
      name: `Sol peint · ${g.label}`,
      desc: "Texture peinte au pinceau dans l'éditeur de terrain.",
      kind: "tile", tile: g.tile, size: [1, 1], ground: true,
    })),
  ], []); // eslint-disable-line
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);

  const filtered = editable.filter(
    (i) => !query || i.name.toLowerCase().includes(query.toLowerCase()) || i.id.includes(query.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-3xl h-[92vh] sm:h-[80vh] bg-card rounded-t-3xl sm:rounded-3xl border overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-primary" />
            <span className="font-head font-bold">{t("adminimages.images_des_batiments")}</span>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 flex min-h-0">
          <div className="w-full sm:w-64 border-r flex flex-col shrink-0">
            <div className="p-2 border-b">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("adminimages.chercher_un_batiment")}
                  className="w-full rounded-lg border bg-background pl-8 pr-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div key={tick} className={`flex-1 overflow-y-auto ${selected ? "hidden sm:block" : ""}`}>
              {filtered.map((item) => {
                const ov = overrideFor(item.id);
                const [w, d] = item.size || [1, 1];
                return (
                  <button key={item.id} type="button" onClick={() => setSelected(item)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border-b hover:bg-muted transition ${
                      selected?.id === item.id ? "bg-muted" : ""
                    }`}>
                    <div className="w-9 h-9 shrink-0 relative">
                      <Sprite item={item} size={36} />
                      {ov && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-card" />}
                    </div>
                    <span className="text-[13px] truncate">
                      {item.name}
                      {(w > 1 || d > 1) && (
                        <span className="text-muted-foreground"> · {w}×{d}</span>
                      )}
                    </span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="p-4 text-xs text-muted-foreground">{t("adminimages.aucun_resultat")}</p>
              )}
            </div>
          </div>

          <div className={`flex-1 min-w-0 ${selected ? "flex" : "hidden sm:flex"} flex-col`}>
            {!selected ? (
              <div className="flex-1 grid place-items-center text-sm text-muted-foreground p-6 text-center">
                {t("adminimages.choisis_un_batiment_dans_la_liste")}
              </div>
            ) : (
              <Editor
                key={selected.id}
                item={selected}
                onBack={() => setSelected(null)}
                onSaved={async () => {
                  await refreshOverrides();
                  setTick((n) => n + 1);
                  onChanged?.();
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Editor({ item, onBack, onSaved }) {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const [w, d] = item.size || [1, 1];
  const footprint = Math.max(1, w, d);

  const [normalized, setNormalized] = useState(null); // data URL 256×352
  const [bbox, setBbox] = useState(null);
  const [anchor, setAnchor] = useState(DEFAULT_ANCHOR);
  const [scale, setScale] = useState(footprint);
  const [isCube, setIsCube] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const existing = overrideFor(item.id);
  useEffect(() => {
    if (existing) {
      setAnchor(existing.anchorY);
      setIsCube(!!existing.isCube);
      setScale(existing.scale || footprint);
      setBbox(existing.bbox || null);
    }
  }, [existing, footprint]);

  // Ramène n'importe quelle image au gabarit standard du pack
  // (256×352, sujet centré), puis MESURE sa boîte englobante : c'est
  // elle qui permettra aux vignettes de la boutique et de l'éditeur de
  // cadrer l'image comme une tuile Kenney, à taille constante.
  const normalize = (file) => {
    const img = new Image();
    img.onload = async () => {
      const c = document.createElement("canvas");
      c.width = IMG_W; c.height = IMG_H;
      const ctx = c.getContext("2d");
      const s = Math.min((IMG_W - 20) / img.width, (IMG_H - 30) / img.height);
      const iw = img.width * s, ih = img.height * s;
      ctx.drawImage(img, (IMG_W - iw) / 2, IMG_H - ih - 10, iw, ih);
      const url = c.toDataURL("image/png");
      setNormalized(url);
      setAnchor(DEFAULT_ANCHOR);
      setScale(footprint);
      setMsg(null);
      setBbox(await measureBBox(url));
    };
    img.src = URL.createObjectURL(file);
  };

  const onPick = (e) => { const f = e.target.files?.[0]; if (f) normalize(f); };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) normalize(f);
  };

  // ── aperçu calibré : mêmes maths que le moteur de rendu, donc ce
  //    qu'on voit ici EST ce que l'île affichera. ────────────────
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const W = c.width, H = c.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#2a4a63";
    ctx.fillRect(0, 0, W, H);

    // Vue resserrée pour les grandes emprises, sinon le sprite déborde.
    const S = 1.15 / Math.max(1, footprint * 0.72);
    const cx = W / 2, cyGround = H - 40;

    // On dessine TOUTE l'emprise en losanges : on voit d'un coup d'œil
    // si l'image couvre bien les cases qu'elle est censée occuper.
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = "#ffffff88";
    ctx.lineWidth = 1.5;
    for (let i = 0; i < w; i++)
      for (let j = 0; j < d; j++) {
        const gx = i - (w - 1) / 2, gy = j - (d - 1) / 2;
        const x = cx + (gx - gy) * (ISO_W / 2) * S;
        const y = cyGround + (gx + gy) * (ISO_H / 2) * S;
        ctx.beginPath();
        ctx.moveTo(x, y - (ISO_H / 2) * S); ctx.lineTo(x + (ISO_W / 2) * S, y);
        ctx.lineTo(x, y + (ISO_H / 2) * S); ctx.lineTo(x - (ISO_W / 2) * S, y);
        ctx.closePath(); ctx.stroke();
      }
    ctx.restore();

    const src = normalized || (existing ? existing.url : null);
    if (src) {
      const img = new Image();
      img.onload = () => {
        const m = S * scale;
        ctx.drawImage(img, cx - ANCHOR_X * m, cyGround - anchor * m, IMG_W * m, IMG_H * m);
        ctx.strokeStyle = "#ff2d55"; ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(cx, cyGround - (ISO_H / 2) * S); ctx.lineTo(cx + (ISO_W / 2) * S, cyGround);
        ctx.lineTo(cx, cyGround + (ISO_H / 2) * S); ctx.lineTo(cx - (ISO_W / 2) * S, cyGround);
        ctx.closePath(); ctx.stroke();
      };
      img.src = src;
    }
  }, [normalized, anchor, scale, existing, w, d, footprint]);

  const save = async () => {
    const src = normalized || existing?.url;
    if (!src) return;
    setBusy(true); setMsg(null);
    try {
      const bb = bbox || (await measureBBox(src));
      await api.post("/oasis/admin/image-override", {
        itemId: item.id, imageDataUrl: src, anchorY: anchor,
        isCube, scale, bbox: bb,
      });
      await onSaved();
      setMsg({ ok: true, text: "Enregistré." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Échec de l'enregistrement." });
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.delete(`/oasis/admin/image-override/${item.id}`);
      await onSaved();
      setNormalized(null);
      setMsg({ ok: true, text: "Image Kenney d'origine restaurée." });
    } catch {
      setMsg({ ok: false, text: "Échec de la réinitialisation." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b sm:hidden">
        <button type="button" onClick={onBack} className="text-sm text-muted-foreground">{t("adminimages.retour")}</button>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <h3 className="font-head font-bold">{item.name}</h3>
          <p className="text-xs text-muted-foreground">
            {item.desc}
            {(w > 1 || d > 1) && ` · emprise ${w}×${d} : l'image remplace la structure entière.`}
          </p>
        </div>

        {/* ── Partir de l'image existante ──────────────────────────
            On ne dessine presque jamais un bâtiment depuis une page
            blanche : on récupère celui du pack, on le retouche, on le
            renvoie. Sans ces boutons il fallait aller chercher le PNG
            à la main dans /kenney-desert, en devinant son nom de
            fichier — d'où ces liens directs.
            Le gabarit vierge, lui, contient les repères de la case
            (losange du sol + cube) pour dessiner à la bonne échelle. */}
        <BaseImages item={item} existing={existing} />

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`rounded-2xl border-2 border-dashed p-4 text-center cursor-pointer transition ${
            dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:bg-muted"
          }`}
        >
          <Upload size={20} className="mx-auto mb-1.5 text-muted-foreground" />
          <p className="text-sm font-medium">{t("adminimages.glisse_une_image_ici_ou_clique")}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{t("adminimages.png_avec_fond_transparent_idealement")}</p>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            {t("adminimages.apercu_cale_sur_la_grille")}
          </div>
          <canvas ref={canvasRef} width={340} height={260}
            className="w-full rounded-2xl border bg-muted" />
          <p className="text-[11px] text-muted-foreground mt-1.5">
            {t("adminimages.les_losanges_sont_les_cases_sur")}
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            <span>{t("admincreate.hauteur_du_calage")}</span>
            <span className="tabular-nums normal-case font-normal">{Math.round(anchor)}</span>
          </div>
          <input type="range" min={80} max={420} step={1} value={anchor}
            onChange={(e) => setAnchor(+e.target.value)}
            className="w-full accent-primary" />
          <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
            <span>{t("adminimages.l_image_monte")}</span>
            <span>{t("adminimages.l_image_descend")}</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            <span>{t("adminimages.taille_de_l_image")}</span>
            <span className="tabular-nums normal-case font-normal">×{scale.toFixed(2)}</span>
          </div>
          <input type="range" min={0.5} max={6} step={0.05} value={scale}
            onChange={(e) => setScale(+e.target.value)}
            className="w-full accent-primary" />
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t("adminimages.prereglee_sur_l_emprise")}{footprint} case{footprint > 1 ? "s" : ""}{t("adminimages.la_hauteur_de_calage_suit_automatiquement")}
          </p>
        </div>

        <label className="flex items-center gap-2.5 rounded-xl border p-3 cursor-pointer">
          <input type="checkbox" checked={isCube} onChange={(e) => setIsCube(e.target.checked)} className="accent-primary" />
          <div>
            <div className="text-sm font-semibold">{t("admincreate.bloc_plein")}</div>
            <div className="text-[11px] text-muted-foreground">
              {t("adminimages.coche_remplace_le_sol_batiments_murs")}
            </div>
          </div>
        </label>

        {msg && (
          <p className={`text-xs flex items-center gap-1.5 ${msg.ok ? "text-emerald-600" : "text-destructive"}`}>
            {msg.ok && <Check size={13} />} {msg.text}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={save} disabled={busy || (!normalized && !existing)}
            className="flex-1 rounded-2xl bg-primary text-primary-foreground py-2.5 text-sm font-bold disabled:opacity-40 hover:brightness-110 transition">
            {t("lessoneditor.enregistrer")}
          </button>
          {existing && (
            <button type="button" onClick={revert} disabled={busy}
              title={t("adminimages.revenir_a_l_image_d_origine")}
              className="rounded-2xl border px-4 py-2.5 text-sm font-semibold hover:bg-muted transition flex items-center gap-1.5">
              <RotateCcw size={14} /> {t("practicealphabet.reinitialiser")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// IMAGES DE DÉPART
//
// Trois choses téléchargeables :
//   · l'image actuellement utilisée (celle qu'on voit sur l'île) ;
//   · la ou les tuiles d'origine du pack, pour une structure composée
//     de plusieurs blocs on les propose une par une ;
//   · un gabarit vierge 256×352 avec les repères de la case, pour
//     dessiner directement à la bonne échelle et au bon calage.
//
// Le gabarit est généré à la volée : pas de fichier à maintenir, et
// ses repères sont calculés avec les mêmes constantes que le moteur
// de rendu — ils ne peuvent donc pas se désynchroniser.
// ─────────────────────────────────────────────────────────────
function BaseImages({ item, existing }) {
  const grab = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Tuiles d'origine réellement utilisées par cet objet, sans doublon.
  const baseTiles = [];
  const parts = item.parts || [{ tile: item.tile }];
  for (const p of parts) {
    if (p.tile && TILE_FILES[p.tile] && !baseTiles.includes(p.tile)) baseTiles.push(p.tile);
  }

  const template = () => {
    const c = document.createElement("canvas");
    c.width = IMG_W; c.height = IMG_H;
    const ctx = c.getContext("2d");
    const cx = IMG_W / 2 - 0.5;
    const top = 182.5;              // face supérieure du bloc
    const base = top + CUBE_H;      // sa base
    const hw = ISO_W / 2, hh = ISO_H / 2;

    const diamond = (y, color, dash) => {
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      if (dash) ctx.setLineDash(dash);
      ctx.beginPath();
      ctx.moveTo(cx, y - hh); ctx.lineTo(cx + hw, y);
      ctx.lineTo(cx, y + hh); ctx.lineTo(cx - hw, y);
      ctx.closePath(); ctx.stroke();
      ctx.restore();
    };
    diamond(base, "rgba(255,45,85,.9)");            // sol : la case
    diamond(top, "rgba(56,189,248,.9)", [6, 5]);    // dessus du cube
    ctx.save();
    ctx.strokeStyle = "rgba(56,189,248,.55)";
    ctx.lineWidth = 2;
    for (const dx of [-hw, 0, hw]) {
      ctx.beginPath();
      ctx.moveTo(cx + dx, top + (dx === 0 ? hh : 0));
      ctx.lineTo(cx + dx, base + (dx === 0 ? hh : 0));
      ctx.stroke();
    }
    ctx.restore();
    grab(c.toDataURL("image/png"), "gabarit-oasis-256x352.png");
  };

  const btn = "inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[12px] font-semibold hover:bg-muted transition";

  return (
    <div className="rounded-2xl border p-3 space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {t("adminimages.partir_d_une_image_existante")}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {existing && (
          <button type="button" className={btn}
            onClick={() => grab(existing.url, `${item.id}-actuel.png`)}>
            <Download size={13} /> {t("adminimages.image_actuelle")}
          </button>
        )}
        {baseTiles.map((t) => (
          <button key={t} type="button" className={btn}
            onClick={() => grab(`${BASE}/${TILE_FILES[t]}.png`, `${TILE_FILES[t]}.png`)}>
            <Download size={13} /> {baseTiles.length > 1 ? TILE_FILES[t] : "Image d'origine"}
          </button>
        ))}
        <button type="button" className={btn} onClick={template}>
          <Download size={13} /> {t("adminimages.gabarit_vierge")}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {t("adminimages.le_gabarit_fait_256_352_avec")}
      </p>
    </div>
  );
}
